import { useState, useEffect, useCallback } from 'react'

interface LocationState {
  hasPermission: boolean
  isChecking: boolean
  city: string | null
  error: string | null
}

export const useLocationPermission = () => {
  const [state, setState] = useState<LocationState>({
    hasPermission: false,
    isChecking: false,
    city: null,
    error: null,
  })

  // Check if location permission is already granted
  useEffect(() => {
    checkLocationPermission()
  }, [])

  const checkLocationPermission = useCallback(async () => {
    try {
      // Check if geolocation is supported
      if (!navigator.geolocation) {
        setState(prev => ({
          ...prev,
          hasPermission: false,
          error: 'Geolocation not supported',
        }))
        return
      }

      // Check permission state
      if (navigator.permissions) {
        const result = await navigator.permissions.query({ name: 'geolocation' })

        if (result.state === 'granted') {
          // Already granted, get city
          await getCurrentCity()
        } else {
          setState(prev => ({ ...prev, hasPermission: false }))
        }

        // Listen for permission changes
        result.addEventListener('change', () => {
          if (result.state === 'granted') {
            getCurrentCity()
          } else {
            setState(prev => ({ ...prev, hasPermission: false, city: null }))
          }
        })
      } else {
        // Fallback: Try to get location directly
        navigator.geolocation.getCurrentPosition(
          () => {
            getCurrentCity()
          },
          () => {
            setState(prev => ({ ...prev, hasPermission: false }))
          }
        )
      }
    } catch (error) {
      console.error('Error checking location permission:', error)
      setState(prev => ({ ...prev, hasPermission: false }))
    }
  }, [])

  const getCurrentCity = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, isChecking: true }))

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords

          // Reverse geocode to get city
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
            )
            const data = await response.json()

            const city = data.address?.city ||
                        data.address?.town ||
                        data.address?.village ||
                        data.address?.state ||
                        'Unknown'

            setState({
              hasPermission: true,
              isChecking: false,
              city,
              error: null,
            })

            // Cache the city
            localStorage.setItem('user_city', city)
          } catch (error) {
            console.error('Reverse geocoding failed:', error)
            setState({
              hasPermission: true,
              isChecking: false,
              city: 'Unknown',
              error: null,
            })
          }
        },
        (error) => {
          console.error('Geolocation error:', error)
          setState({
            hasPermission: false,
            isChecking: false,
            city: null,
            error: error.message,
          })
        },
        {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 300000, // 5 minutes cache
        }
      )
    } catch (error) {
      console.error('Error getting current city:', error)
      setState(prev => ({ ...prev, isChecking: false, hasPermission: false }))
    }
  }, [])

  const requestLocationPermission = useCallback(async (): Promise<boolean> => {
    try {
      setState(prev => ({ ...prev, isChecking: true }))

      return new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            await getCurrentCity()
            resolve(true)
          },
          (error) => {
            console.error('Location permission denied:', error)
            setState({
              hasPermission: false,
              isChecking: false,
              city: null,
              error: error.message,
            })
            resolve(false)
          },
          {
            enableHighAccuracy: false,
            timeout: 10000,
          }
        )
      })
    } catch (error) {
      console.error('Error requesting location permission:', error)
      setState(prev => ({ ...prev, isChecking: false }))
      return false
    }
  }, [getCurrentCity])

  return {
    hasPermission: state.hasPermission,
    isChecking: state.isChecking,
    city: state.city,
    error: state.error,
    requestPermission: requestLocationPermission,
    checkPermission: checkLocationPermission,
  }
}

export default useLocationPermission
